import { useEffect, useRef, useState } from "react";
import { getToken } from "../utils/helpers";
import { createLocalRevisionWatcher } from "./cicdLocalRevision";

const streams = new Map();
const FALLBACK_MS = 60000;
const INACTIVITY_MS = 90000;
const READ_INTERVAL_MS = 2000;
const LEGACY_RECONNECT_INTERVAL_MS = 15000;
const eventMetrics = { timeout: 0, serverEnd: 0, visibility: 0, failure: 0, notifyAll: 0 };
let metricsAt = Date.now();
export const cicdEventMetrics = () => ({ ...eventMetrics });
function recordMetric(key) {
  eventMetrics[key]++;
  if (Date.now() - metricsAt >= 60000) {
    // Counts only, no token, Space, URL or event payload. No telemetry API call.
    console.debug("cicd.browser.events", { ...eventMetrics });
    metricsAt = Date.now(); Object.keys(eventMetrics).forEach(name => { eventMetrics[name] = 0; });
  }
}
export const reconnectDelay = (backoff, random = Math.random()) => Math.round(backoff + random * 10000);
const visible = () => typeof document === "undefined" || document.visibilityState !== "hidden";

// A page can observe a batch and its Edge follow-ups through one scoped connection.
export function subscribeCicdEvents(spaceId, listener, connection = () => {}, { localRevision = false, reconcileOnConnect = false } = {}) {
  let token;
  try { token = getToken(); } catch { return () => {}; }
  if (!spaceId || !token) return () => {};
  const key = JSON.stringify([spaceId, token]);
  let stream = streams.get(key);
  if (!stream) {
    stream = { listeners: new Set(), state: "connecting", stopped: false, revision: null, backoff: 1000, resumeNeeded: !visible(), connectReason: "initial" };
    streams.set(key, stream);
    const state = value => { stream.state = value; stream.listeners.forEach(row => row.connection(value)); };
    const notify = (kind = "all", delay = Math.round(Math.random() * 500), unchangedReconnect = false) => {
      if (kind === "all") recordMetric("notifyAll");
      stream.listeners.forEach(row => {
        const selected = kind === "all" || (kind === "handshake" ? row.reconcileOnConnect
          : kind === "deferred" ? row.deferred : (kind === "local") === row.localRevision);
        if (selected) row.enqueue(delay, kind === "legacy" || (unchangedReconnect && !row.localRevision));
      });
    };
    stream.notify = notify;
    stream.forbid = () => {
      state("forbidden"); clearTimeout(stream.fallbackTimer);
      clearTimeout(stream.reconnectTimer);
      stream.listeners.forEach(row => row.cancel());
      stream.revisionWatcher?.stop(); stream.controller?.abort();
    };
    const fallback = () => {
      clearTimeout(stream.fallbackTimer);
      stream.fallbackTimer = setTimeout(() => {
        if (stream.stopped || stream.state === "forbidden") return;
        if (stream.state !== "connected") notify("legacy");
        fallback();
      }, FALLBACK_MS);
    };
    const connect = async () => {
      if (stream.stopped || stream.state === "forbidden") return;
      if (!visible() || stream.connecting) return;
      stream.connecting = true;
      const controller = new AbortController(); stream.controller = controller;
      let inactivity; stream.abortReason = null;
      const touch = () => { clearTimeout(inactivity); inactivity = setTimeout(() => { stream.abortReason = "timeout"; controller.abort(); }, INACTIVITY_MS); };
      touch();
      try {
        const response = await fetch(`/api/cicd/events?spaceId=${encodeURIComponent(spaceId)}&reason=${stream.connectReason}`, {
          headers: { Authorization: `Bearer ${token}`, Accept: "text/event-stream" }, signal: controller.signal, cache: "no-store",
        });
        if (stream.stopped) return;
        if ([401, 403].includes(response.status)) { stream.forbid(); return; }
        if (!response.ok || !response.body?.getReader) throw new Error("CICD_EVENTS_UNAVAILABLE");
        const reader = response.body.getReader(), decoder = new TextDecoder();
        let buffer = "";
        while (!stream.stopped) {
          const chunk = await reader.read();
          if (chunk.done) break;
          touch(); buffer = (buffer + decoder.decode(chunk.value, { stream: true })).replace(/\r\n/g, "\n");
          if (buffer.length > 65536) throw new Error("CICD_EVENT_TOO_LARGE");
          let separator;
          while ((separator = buffer.indexOf("\n\n")) !== -1) {
            const block = buffer.slice(0, separator); buffer = buffer.slice(separator + 2);
            const event = block.split("\n").find(line => line.startsWith("event:"))?.slice(6).trim();
            if (!["connected", "cicd-update", "authorization"].includes(event)) continue;
            let value;
            try { value = JSON.parse(block.split("\n").filter(line => line.startsWith("data:")).map(line => line.slice(5).trimStart()).join("\n")); } catch { continue; }
            if (value.spaceId !== spaceId) continue;
            if (event === "authorization") { stream.forbid(); return; }
            if (event === "connected") {
              // On initial connect (no prior revision), skip notify — the mount
              // read is already in flight or just completed. Reconnections (prior
              // revision known) notify legacy readers; local readers compare the cursor.
              const reconnect = stream.revision !== null;
              const changed = reconnect && stream.revision !== value.revision;
              stream.revision = value.revision; stream.revisionWatcher?.observe(value.revision);
              stream.backoff = 1000; state("connected");
              const resumed = stream.resumeNeeded; stream.resumeNeeded = false;
              const delay = reconnect ? 150 + Math.round(Math.random() * 3000) : 150;
              if (changed) notify("all");
              else if (reconnect) notify("legacy", delay);
              if (resumed) notify("deferred", delay);
              // Local status readers reconcile after subscription, closing the gap
              // between the mount snapshot and the stream's initial revision.
              notify("handshake", delay, reconnect && !changed);
              stream.revisionWatcher?.check();
            } else if (value.revision !== stream.revision) {
              // Revision is an opaque invalidation cursor. A snapshot handles gaps and reordering.
              stream.revision = value.revision; stream.revisionWatcher?.observe(value.revision); notify();
            }
          }
        }
        if (!stream.stopped) throw new Error("CICD_EVENTS_CLOSED");
      } catch (error) {
        if (!stream.stopped && stream.state !== "forbidden") {
          stream.connectReason = stream.abortReason || (error?.message === "CICD_EVENTS_CLOSED" ? "serverEnd" : "failure");
          recordMetric(stream.connectReason);
          state("reconnecting");
          // Every tab backs off independently after a shared outage. Hidden
          // tabs resume on visibility instead of keeping server subscribers alive.
          const retryDelay = reconnectDelay(stream.backoff);
          if (visible()) stream.reconnectTimer = setTimeout(connect, retryDelay);
          stream.backoff = Math.min(stream.backoff * 2, 30000);
        }
      } finally { stream.connecting = false; clearTimeout(inactivity); }
    };
    stream.visibility = () => {
      if (stream.stopped || stream.state === "forbidden") return;
      if (!visible()) {
        stream.resumeNeeded = true; stream.abortReason = "visibility"; stream.connectReason = "visibility";
        clearTimeout(stream.reconnectTimer);
        stream.controller?.abort();
        return;
      }
      // Preserve deferred work until the handshake; equal revisions do not
      // imply that a reader mounted while hidden has already seen a snapshot.
      if (!stream.connecting && (stream.state !== "connected" || stream.controller?.signal.aborted)) {
        clearTimeout(stream.reconnectTimer);
        stream.reconnectTimer = setTimeout(connect, Math.round(500 + Math.random() * 500));
      }
    };
    if (typeof document !== "undefined") document.addEventListener("visibilitychange", stream.visibility);
    fallback(); Promise.resolve().then(connect);
  }
  const row = { listener, connection, localRevision, reconcileOnConnect, active: true,
    running: false, pending: false, deferred: !visible(), lastStarted: -Infinity, due: null };
  row.cancel = () => { clearTimeout(row.timer); row.timer = null; row.pending = false; row.due = null; row.deferred = false; };
  const canRefresh = () => {
    if (!row.active || stream.stopped || stream.state === "forbidden") return false;
    try { return getToken() === token; } catch { return false; }
  };
  const schedule = () => {
    clearTimeout(row.timer); row.timer = null;
    if (!canRefresh()) { row.cancel(); return; }
    if (!row.pending) return;
    if (!visible()) { row.deferred = true; return; }
    if (row.running) return;
    row.timer = setTimeout(row.refresh, Math.max(0, row.due - Date.now(), row.lastStarted + READ_INTERVAL_MS - Date.now()));
  };
  row.enqueue = (delay = 0, legacy = false) => {
    if (!canRefresh()) { row.cancel(); return; }
    const due = Math.max(Date.now() + delay, row.lastStarted + (legacy ? LEGACY_RECONNECT_INTERVAL_MS : READ_INTERVAL_MS));
    row.due = row.pending ? Math.min(row.due, due) : due;
    row.pending = true;
    schedule();
  };
  row.refresh = async () => {
    row.timer = null;
    if (!canRefresh()) { row.cancel(); return; }
    if (!visible()) { row.deferred = true; return; }
    if (row.running || !row.pending) return;
    row.pending = false; row.due = null; row.deferred = false;
    row.running = true; row.lastStarted = Date.now();
    try { await row.listener({ spaceId, revision: stream.revision }); } catch { /* The consumer displays its read error. */ }
    finally {
      row.running = false;
      // A final event during a slow read still gets one trailing read, even
      // when no further event arrives. New events never postpone that read.
      schedule();
    }
  };
  stream.listeners.add(row); connection(stream.state);
  if (reconcileOnConnect && stream.state === "connected") Promise.resolve().then(() => row.enqueue());
  if (localRevision && !stream.revisionWatcher && stream.state !== "forbidden") {
    stream.revisionWatcher = createLocalRevisionWatcher({ spaceId, token, initialRevision: stream.revision,
      currentToken: () => { try { return getToken(); } catch { return null; } },
      onChange: revision => { stream.revision = revision; stream.notify("local"); }, onForbidden: stream.forbid });
    stream.revisionWatcher.check();
  }
  return () => {
    row.active = false; row.cancel();
    stream.listeners.delete(row);
    if (![...stream.listeners].some(item => item.localRevision)) { stream.revisionWatcher?.stop(); stream.revisionWatcher = null; }
    if (stream.listeners.size) return;
    stream.stopped = true; stream.controller?.abort();
    if (typeof document !== "undefined") document.removeEventListener("visibilitychange", stream.visibility);
    clearTimeout(stream.reconnectTimer); clearTimeout(stream.fallbackTimer);
    streams.delete(key);
  };
}

export default function useCicdEvents(spaceId, onUpdate, enabled = true, { localRevision = false, reconcileOnConnect = false } = {}) {
  const callback = useRef(onUpdate); callback.current = onUpdate;
  let authToken;
  try { authToken = getToken(); } catch { authToken = null; }
  const connectionScope = JSON.stringify([spaceId, authToken, enabled, localRevision, reconcileOnConnect]);
  const [connection, setConnection] = useState(null);
  useEffect(() => {
    if (!enabled || !spaceId) return;
    let active = true, running = false, pending = false;
    let latestSignal;
    const refresh = async signal => {
      latestSignal = signal;
      if (running) { pending = true; return; }
      running = true;
      do {
        pending = false;
        try { await callback.current?.(latestSignal); } catch { /* Consumers retain their read error. */ }
      } while (active && pending);
      running = false;
    };
    const close = subscribeCicdEvents(spaceId, refresh, state => { if (active) setConnection({ scope: connectionScope, state }); }, { localRevision, reconcileOnConnect });
    return () => { active = false; close(); };
  }, [spaceId, enabled, authToken, localRevision, reconcileOnConnect, connectionScope]);
  return connection?.scope === connectionScope ? connection.state : "connecting";
}
